import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import "./header.css";

function Header() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll event to add shadow when scrolled
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScroll = (e, sectionId) => {
    e.preventDefault();
    
    // Close mobile menu after clicking a link
    setMobileMenuOpen(false);
    
    // Check if we're already on the home page
    if (location.pathname !== '/') {
      // If not on home page, navigate to home with the section as hash
      navigate(`/#${sectionId}`);
      return;
    }
    
    // If already on home page, scroll to the section
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 15; // Adjust based on your header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <header className="header">
      {/* Top Bar - Blue Stripe */}
      <div className="top-bar">
        <div className="container">
          <div className="top-bar-content">
            {/* Top Row: Phone and Language Switcher */}
            <div className="top-row">
              <div className="contact-info">
                <a href={`tel:${t("footer.phoneNumber")}`} className="phone">
                  <i className="fas fa-phone-alt"></i> {t("footer.phoneNumber")}
                </a>
                <span className="divider">|</span>
                <a href="https://www.google.com/maps/place/Dr+William+Mok+Orthodontic+Center/@22.2819611,114.1568384,750m/data=!3m2!1e3!4b1!4m6!3m5!1s0x34040064bd51fc75:0xf32c6d3ceb8cd52c!8m2!3d22.2819611!4d114.1568384!16s%2Fg%2F1xg5q8hr?entry=ttu&g_ep=EgoyMDI2MDEwNi4wIKXMDSoKLDEwMDc5MjA3M0gBUAM%3D" className="location desktop-only">
                  <i className="fas fa-map-marker-alt"></i> 19 Des Voeux Road
                </a>
                <span className="divider desktop-only">|</span>
                <a href="/weather" className="bad-weather desktop-only">
                  <i className="fas fa-cloud-rain"></i> {t("footer.weather")}
                </a>
              </div>
              <div className="language-switcher">
                <button
                  onClick={() => i18n.changeLanguage("en")}
                  className={i18n.language === "en" ? "active" : ""}
                >
                  EN
                </button>
                <span className="divider">|</span>
                <button
                  onClick={() => i18n.changeLanguage("zh")}
                  className={i18n.language === "zh" ? "active" : ""}
                >
                  中文
                </button>
              </div>
            </div>
            
            {/* Bottom Row: Location and Weather */}
            <div className="bottom-row">
              <a href="https://www.google.com/maps/place/Dr+William+Mok+Orthodontic+Center/@22.2819611,114.1568384,750m/data=!3m2!1e3!4b1!4m6!3m5!1s0x34040064bd51fc75:0xf32c6d3ceb8cd52c!8m2!3d22.2819611!4d114.1568384!16s%2Fg%2F1xg5q8hr?entry=ttu&g_ep=EgoyMDI2MDEwNi4wIKXMDSoKLDEwMDc5MjA3M0gBUAM%3D" className="location">
                <i className="fas fa-map-marker-alt"></i> 19 Des Voeux Road
              </a>
              <a href="/weather" className="bad-weather">
                <i className="fas fa-cloud-rain"></i> {t("footer.weather")}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className={`main-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <a href="/" onClick={(e) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}>
                <img
                  src="./assets/images/logo.png"
                  alt={t("header.logoAlt")}
                  width="350"
                  height="90"
                />
              </a>
            </div>

            {/* Mobile Menu Toggle */}
            <button 
              className={`mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            
            {/* Navigation and CTA Button Container */}
            <div className={`nav-container ${mobileMenuOpen ? 'mobile-open' : ''}`}>
              <nav className="main-nav">
                <ul>
                  <li>
                    <a
                      href="#about"
                      onClick={(e) => handleScroll(e, "about")}
                      className="nav-link"
                    >
                      {t("nav.about")}
                    </a>
                  </li>
                  <li>
                    <a
                      href="#team"
                      onClick={(e) => handleScroll(e, "team")}
                      className="nav-link"
                    >
                      {t("nav.team")}
                    </a>
                  </li>
                  <li>
                    <a
                      href="#services"
                      onClick={(e) => handleScroll(e, "services")}
                      className="nav-link"
                    >
                      {t("nav.services")}
                    </a>
                  </li>
                  
                  <li>
                    <a
                      href="#whattoexpect"
                      onClick={(e) => handleScroll(e, "whattoexpect")}
                      className="nav-link"
                    >
                      {t("nav.whattoexpect")}
                    </a>
                  </li>

                  <li>
                    <a
                      href="#contact"
                      onClick={(e) => handleScroll(e, "contact")}
                      className="nav-link"
                    >
                      {t("nav.contact")}
                    </a>
                  </li>
                </ul>
              </nav>

              <button data-type ={t("header.requestAppointment")} onClick={() => window.location.href = `tel:${t("footer.phoneNumber")}`} className="cta-button">
                <i className="fas fa-phone-alt"></i>{" "}
                {t("header.requestAppointment")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
